import { useMatchRoute } from '@tanstack/react-router'
import { cva, VariantProps } from 'class-variance-authority'
import { cloneElement, forwardRef } from 'react'

import { InternalLinkDestination } from '@/routing/types'
import { InternalLink } from '@/ui/atoms/link/Link'
import { cn } from '@/ui/utils/style'

const buttonVariants = cva(
  cn(
    'typography-label-2 inline-flex h-20 flex-nowrap items-center md:h-10',
    'group gap-2 border-l-4 bg-primary px-4 py-2 md:rounded-sm md:border md:border-primary',
    'border-solid border-opacity-40 transition-colors md:shadow-xs',
    'focus-visible:outline-hidden focus-visible:ring-1',
  ),
  {
    variants: {
      type: {
        savings: cn(
          'hover:border-page-savings/40 active:border-page-savings',
          'hover:shadow-page-savings/15 focus-visible:ring-page-savings',
        ),
        farms: cn(
          'hover:border-page-farms/40 active:border-page-farm',
          'hover:shadow-page-farms/15 focus-visible:ring-page-farms',
        ),
        sparkLend: cn(
          'hover:border-page-sparklend/40 active:border-page-sparklend',
          'hover:shadow-page-sparklend/15 focus-visible:ring-page-sparklend',
        ),
        sparkToken: cn(
          'hover:border-page-spark-token/40 active:border-page-spark-token',
          'hover:shadow-page-spark-token/15 focus-visible:ring-page-spark-token',
        ),
        swap: cn(
          'hover:border-page-swap/40 active:border-page-swap',
          'hover:shadow-page-swap/15 focus-visible:ring-page-swap',
        ),
      },
      isActive: {
        false: '',
      },
      isHighlighted: {
        false: '',
      },
    },
    compoundVariants: [
      { isActive: false, isHighlighted: false, class: 'border-base-white' },
      { type: 'savings', isHighlighted: true, class: 'border-page-savings/40 shadow-page-savings/15' },
      { type: 'farms', isHighlighted: true, class: 'border-page-farms/40 shadow-page-farms/15' },
      { type: 'sparkLend', isHighlighted: true, class: 'border-page-sparklend/40 shadow-page-sparklend/15' },
      { type: 'sparkToken', isHighlighted: true, class: 'border-page-spark-token/40 shadow-page-spark-token/15' },
      { type: 'swap', isHighlighted: true, class: 'border-page-swap/40 shadow-page-swap/15' },
      // @note overrides didn't work without important flag
      { type: 'savings', isActive: true, class: 'border-page-savings!' },
      { type: 'farms', isActive: true, class: 'border-page-farms!' },
      { type: 'sparkLend', isActive: true, class: 'border-page-sparklend!' },
      { type: 'sparkToken', isActive: true, class: 'border-page-spark-token!' },
      { type: 'swap', isActive: true, class: 'border-page-swap!' },
    ],
    defaultVariants: {
      isActive: false,
      isHighlighted: false,
    },
  },
)

type ButtonType = NonNullable<VariantProps<typeof buttonVariants>['type']>

export interface TopbarButtonProps {
  postfixSlot?: React.JSX.Element
  prefixIcon?: React.JSX.Element
  className?: string
  active?: boolean
  highlighted?: boolean
  label: string
  to?: InternalLinkDestination
  activeRoutes?: InternalLinkDestination[]
  onClick?: React.MouseEventHandler<HTMLElement>
  type?: ButtonType
}

export const TopbarButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, TopbarButtonProps>(
  (
    {
      postfixSlot,
      prefixIcon,
      className: _className,
      label,
      to,
      activeRoutes,
      type,
      active,
      highlighted,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const matchRoute = useMatchRoute()
    const isMainRouteActive = to ? !!matchRoute({ to, fuzzy: true }) : false
    const isAdditionalRouteActive = activeRoutes?.some((route) => !!matchRoute({ to: route, fuzzy: true })) ?? false
    const isActive = isMainRouteActive || isAdditionalRouteActive || active

    const className = buttonVariants({ type, isActive, isHighlighted: highlighted })

    const content = (
      <>
        {prefixIcon}
        <span className="inline top-bar-xl:inline md:hidden">{label}</span>

        {postfixSlot &&
          cloneElement(postfixSlot, {
            className: cn('h-5 transition-colors group-hover:text-secondary', postfixSlot.props.className),
          })}
      </>
    )

    if (to) {
      return (
        <InternalLink
          to={to}
          variant="unstyled"
          className={className}
          onClick={onClick}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {content}
        </InternalLink>
      )
    }

    return (
      <button type="button" className={className} onClick={onClick} ref={ref as React.Ref<HTMLButtonElement>} {...rest}>
        {content}
      </button>
    )
  },
)
